import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import {
  Code2,
  FileQuestion,
  Clock,
  CalendarClock,
  GraduationCap,
  BookOpen,
  CheckCircle2,
  PlayCircle,
  Circle,
  AlertTriangle,
  Award,
} from "lucide-react";
import apiClient from "../../../utils/apiClient";
import { fmtDate, PanelLoading, PanelEmpty } from "./panelShared";

/**
 * ChallengesPanel — coding challenges created for the student's batch.
 * These are batch-scoped coding assignments (exam / practice), NOT the
 * institute-wide self-practice bank.
 *
 * Data:  GET /assignment-sessions/list?studentId=  →  { assignments }
 *        (server scopes to the student's batch & published challenges)
 * Start: POST /assignment-sessions/start/:assignmentId  →  { session: { _id } }
 * Open:  /s/assignment-session/:sessionId  (the coding workspace)
 */

const STATUS_META = {
  submitted: { label: "Completed", Icon: CheckCircle2, cls: "is-completed" },
  active: { label: "In Progress", Icon: PlayCircle, cls: "is-progress" },
  pending: { label: "Not Started", Icon: Circle, cls: "is-pending" },
  expired: { label: "Expired", Icon: AlertTriangle, cls: "is-expired" },
};

const ChallengeCard = ({ a, onStart, starting }) => {
  const navigate = useNavigate();
  const meta = STATUS_META[a.sessionStatus] || STATUS_META.pending;
  const StatusIcon = meta.Icon;
  const isPractice = a.assignmentType === "practice";
  const TypeIcon = isPractice ? BookOpen : GraduationCap;

  let action;
  if (a.sessionStatus === "submitted" && a.sessionId) {
    action = (
      <button
        type="button"
        className="ml-acard__btn ml-acard__btn--ghost"
        onClick={() => navigate(`/s/assignment-session/${a.sessionId}`)}
      >
        View Result
      </button>
    );
  } else if (a.sessionStatus === "active" && a.sessionId) {
    action = (
      <button
        type="button"
        className="ml-acard__btn ml-acard__btn--primary"
        onClick={() => navigate(`/s/assignment-session/${a.sessionId}`)}
      >
        Resume
      </button>
    );
  } else if (a.sessionStatus === "expired") {
    action = (
      <button type="button" className="ml-acard__btn ml-acard__btn--ghost" disabled>
        Closed
      </button>
    );
  } else {
    action = (
      <button
        type="button"
        className="ml-acard__btn ml-acard__btn--primary"
        onClick={() => onStart(a._id)}
        disabled={starting}
      >
        {starting ? "Starting…" : "Start Challenge"}
      </button>
    );
  }

  return (
    <article className="ml-acard">
      <div className="ml-acard__head">
        <span className={`ml-acard__type ${isPractice ? "is-practice" : "is-exam"}`}>
          <TypeIcon size={13} /> {isPractice ? "Practice" : "Exam"}
        </span>
        <span className={`ml-status ${meta.cls}`}>
          <StatusIcon size={13} /> {meta.label}
        </span>
      </div>

      <h4 className="ml-acard__title" title={a.title}>
        {a.title}
      </h4>

      <div className="ml-acard__meta">
        {a.numQuestions != null && (
          <span>
            <FileQuestion size={13} /> {a.numQuestions} {a.numQuestions === 1 ? "problem" : "problems"}
          </span>
        )}
        {a.timeLimit > 0 && (
          <span>
            <Clock size={13} /> {a.timeLimit} min
          </span>
        )}
        {a.endDate && (
          <span>
            <CalendarClock size={13} /> Due {fmtDate(a.endDate)}
          </span>
        )}
      </div>

      <div className="ml-acard__foot">
        {a.sessionStatus === "submitted" && a.scoreOutOf100 != null ? (
          <span className="ml-acard__score">
            <Award size={14} /> {a.scoreOutOf100}%
          </span>
        ) : (
          <span />
        )}
        {action}
      </div>
    </article>
  );
};

const ChallengesPanel = ({ studentId }) => {
  const [loading, setLoading] = useState(true);
  const [challenges, setChallenges] = useState([]);
  const [startingId, setStartingId] = useState(null);
  const navigate = useNavigate();

  const load = useCallback(() => {
    if (!studentId) {
      setLoading(false);
      return undefined;
    }
    let alive = true;
    setLoading(true);
    apiClient
      .get("/assignment-sessions/list", { params: { studentId, limit: 50 } })
      .then(({ data }) => {
        if (alive) setChallenges(data?.assignments || []);
      })
      .catch(() => {
        if (alive) setChallenges([]);
      })
      .finally(() => {
        if (alive) setLoading(false);
      });
    return () => {
      alive = false;
    };
  }, [studentId]);

  useEffect(() => load(), [load]);

  const handleStart = useCallback(
    async (assignmentId) => {
      setStartingId(assignmentId);
      try {
        const res = await apiClient.post(`/assignment-sessions/start/${assignmentId}`, { _id: studentId });
        const sid = res?.data?.session?._id;
        if (sid) navigate(`/s/assignment-session/${sid}`);
      } catch {
        /* surfaced by the global interceptor */
      }
      setStartingId(null);
    },
    [studentId, navigate]
  );

  if (loading) return <PanelLoading />;
  if (!challenges.length)
    return <PanelEmpty icon={Code2}>Coding challenges assigned to your batch will appear here.</PanelEmpty>;

  return (
    <div className="ml-cardgrid">
      {challenges.map((a) => (
        <ChallengeCard key={a._id} a={a} onStart={handleStart} starting={startingId === a._id} />
      ))}
    </div>
  );
};

export default ChallengesPanel;
